import {
    EventrDate,
    IRoom,
    ISubscription,
    spError,
    spErrorTags,
    spLogTags,
} from '@eventr/common'
import ApiClient from '@eventr/common/lib/httpRequests/httpRequest'
import { Signal, useSignal } from '@preact/signals-react'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useShallow } from 'zustand/react/shallow'
import {
    IRCUser,
    IRCUserInfo,
    RocketAPI,
    RocketChatBodyType,
    RocketCreateChannelBodyType,
    RocketCreateDirectChannelBodyType,
    RocketRemoveUsersFromChannelBodyType,
} from '../../data/Rocket'
import i18n from '../../i18n/i18next'
import { bs, useBoundStore } from '../../state/bound.store'
import { defaultAddSnack } from '../../state/overlayState/overlayContext'
import { liveToken } from '../../state/rocketchatState/rocketchatSignals'
import {
    getMobileOperatingSystem,
    getRocketChatUrl,
    getRocketChatWebsocket,
} from '../../utils'
import { isConnectivityAvailable } from '../connectivity/connectivityListener'
import { PushTokenSignal } from '../push/pushProvider'
import EmbeddedChatApi from './chatBody/EmbeddedChatApi'
import { chatStore, getChatCache } from './chatStore'
import { ChatTypes } from './Components/ChatCard'
import { ROCKET_ENDPOINTS } from './endpoints'

export interface IRoomWithType {
    room: IRoom | ISubscription
    type: ChatTypes
}

export type chatViews =
    | 'default'
    | 'chatInfo'
    | 'chatMembers'
    | 'chatNotifications'
    | 'manageGroupChannel'

export const createGroupChannel = async (data: RocketCreateChannelBodyType) => {
    spLogTags(`Creating new group channel: ${data.channel_name}`, [
        'rocketchat',
    ])
    const resp = await RocketAPI.createGroupChannel(data)
    spLogTags(resp, ['rocketchat'])
    return resp
}

export const deleteGroupChannel = async (channelId: string) => {
    spLogTags(`Deleting group channel: ${channelId}`, ['rocketchat'])
    const resp = await RocketAPI.deleteGroupChannel({ roomId: channelId })
    spLogTags(resp, ['rocketchat'])
    return resp
}

export const createChannel = async (data: RocketChatBodyType) => {
    spLogTags(`Creating new channel: ${data.channel_name}`, ['rocketchat'])
    const resp = await RocketAPI.createChannel(data)
    spLogTags(resp, ['rocketchat'])
    return resp
}

export const removeUsersFromGroupChannel = async (
    data: RocketRemoveUsersFromChannelBodyType
) => {
    const resp = await RocketAPI.removeUsersFromGroupChannel(data)
    spLogTags(resp, ['rocketchat'])
    return resp
}

export const addUsersToChannel = async (
    data: RocketRemoveUsersFromChannelBodyType
) => {
    const resp = await RocketAPI.addUsersToChannel(data)
    spLogTags(resp, ['rocketchat'])
    return resp
}

interface IRocketChatContext {
    createDirectChannel: (
        data: RocketCreateDirectChannelBodyType
    ) => Promise<any>
    openChat: (rid?: string, type?: string) => void
    setLoadingChat: (chatLoading: boolean) => void
    setSearchInput: (searchInput: string) => void
    getRooms: () => void
    setRooms: (room: string) => void
    getSubscribedRooms: () => void
    markAsRead: (rid: string) => any
    userRooms: IRoom[]
    userSubscriptions: ISubscription[]
    rcUserList: IRCUser[]
    rcUserInfo: IRCUserInfo | undefined
    loadingChat: boolean
    isActivated: boolean
    searchInput: string | undefined
    chatView: Signal<chatViews> | null
}

const RocketChatContext = createContext<IRocketChatContext>({
    createDirectChannel: (data: RocketCreateDirectChannelBodyType) =>
        Promise.resolve({}),
    openChat: () => {},
    setLoadingChat: () => {},
    setSearchInput: () => {},
    getRooms: () => {},
    setRooms: () => {},
    getSubscribedRooms: () => {},
    markAsRead: () => Promise.resolve({}),
    userRooms: [],
    userSubscriptions: [],
    rcUserList: [],
    rcUserInfo: undefined,
    loadingChat: false,
    isActivated: false,
    searchInput: undefined,
    chatView: null,
})

export const rocketchatBaseApi = new Signal<EmbeddedChatApi | null>(null)

export const RocketChatProvider = (props: { children: React.ReactNode }) => {
    const { orgConfig, wpUser, selectedOrg, rcTokens } = useBoundStore(
        useShallow((s) => {
            return {
                orgConfig: s.orgConfig,
                wpUser: s.wpUser,
                selectedOrg: s.selectedOrg,
                rcTokens: s.rcTokens,
            }
        })
    )

    const apiClient = useMemo(() => new ApiClient({ baseURL: '' }), [])

    const [rcUrl, setRcUrl] = useState<string>(
        selectedOrg ? getRocketChatWebsocket(selectedOrg.name) : ''
    )

    const chatView = useSignal<chatViews>('default')

    const [userRooms, setUserRooms] = useState<IRoom[]>()
    const [userSubscriptions, setUserSubscriptions] =
        useState<ISubscription[]>()

    const [rcUserList, setRcUserList] = useState<IRCUser[]>()
    const [rcUserInfo, setRcUserInfo] = useState<IRCUserInfo | undefined>(
        undefined
    )

    const [initialiseRooms, setInitialiseRooms] = useState<boolean>(true)
    const [loadingChat, setLoadingChat] = useState<boolean>(true)
    const [isActivated, setIsActivated] = useState<boolean>(false)
    const [searchInput, setSearchInput] = useState<string>('')

    const rocketWebsocket = useRef<WebSocket | null>(null)

    useEffect(() => {
        if (!liveToken.value && wpUser && selectedOrg && selectedOrg.name) {
            try {
                getChatCache(
                    'subscriptions',
                    'rcSubscriptions-' + selectedOrg.name
                ).then((e: any) => {
                    if (!liveToken.value && wpUser) {
                        setUserSubscriptions(e)
                    }
                })
            } catch {
                spLogTags('Could not get rc subscriptions from cache', [
                    'rocketchat',
                ])
            }
        }
    }, [selectedOrg, wpUser, liveToken.value, setUserSubscriptions])

    useEffect(() => {
        if (!liveToken.value && wpUser && selectedOrg?.name) {
            try {
                getChatCache('rooms', 'rcRooms-' + selectedOrg.name).then(
                    (e: any) => {
                        if (!liveToken.value && wpUser) {
                            setUserRooms(e)
                        }
                    }
                )
            } catch {
                spLogTags('Could not get rc rooms from cache', ['rocketchat'])
            }
        }
    }, [selectedOrg, liveToken.value, setUserRooms, wpUser])

    useEffect(() => {
        try {
            if (
                ((!isActivated && orgConfig?.isRocketChatInitialised) ||
                    isActivated === undefined) &&
                orgConfig.isRocketChatInitialised
            ) {
                setIsActivated(orgConfig?.isRocketChatInitialised)
            } else if (
                isActivated &&
                !orgConfig.isRocketChatInitialised &&
                orgConfig.isRocketChatInitialised !== undefined
            ) {
                setIsActivated(orgConfig.isRocketChatInitialised)
            }
            spLogTags(
                'Received isRocketChatInitialised: ' +
                    orgConfig.isRocketChatInitialised,
                ['rocketchat']
            )
        } catch {
            spLogTags('error in setting isRocketChatInitialised', [
                'rocketchat',
                'error',
            ])
        }
    }, [orgConfig])

    rocketchatBaseApi.value = useMemo(() => {
        if (liveToken.value) {
            return new EmbeddedChatApi(
                getRocketChatUrl(liveToken.value.org),
                liveToken?.value?.userId,
                liveToken.value.authToken
            )
        } else return null
    }, [liveToken.value])

    const getTokens = useCallback(
        function () {
            try {
                setLoadingChat(true)
                if (selectedOrg && rcTokens) {
                    const orgToken = rcTokens.find(
                        (x) => x?.org === selectedOrg.name
                    )
                    if (orgToken) {
                        liveToken.value = orgToken
                    } else {
                        spLogTags(
                            'No matching RocketChat token found, will retry in 10 seconds',
                            ['rocketchat']
                        )
                    }
                }
                setRcUrl(
                    selectedOrg ? getRocketChatWebsocket(selectedOrg.name) : ''
                )
            } catch (error) {
                spLogTags(
                    'Error getting rocket token failed',
                    ['ERROR', 'rocketchat'],
                    error
                )
            }
        },
        [selectedOrg, rcTokens, liveToken.value]
    )

    // On org change, or RC init, refresh RC tokens
    useEffect(() => {
        if (isActivated) {
            bs().getRcTokens()
        }
    }, [selectedOrg, isActivated])

    // When RC tokens updates, try to set a valid liveToken
    // If a valid token cannot be found, refresh RC tokens
    useEffect(() => {
        liveToken.value = undefined
        if (isActivated) {
            getTokens()
            if (selectedOrg && !liveToken.value) {
                const interval = setInterval(() => {
                    if (selectedOrg) {
                        bs().getRcTokens()
                    }
                }, 10000)
                return () => clearInterval(interval)
            }
        }
    }, [selectedOrg, getTokens, isActivated])

    //register push token for the rocketchat
    useEffect(() => {
        if ((rcTokens?.length ?? 0) > 0 && PushTokenSignal.value) {
            const os = getMobileOperatingSystem()
            rcTokens.forEach((x) => {
                const pushedTokens = bs().pushedTokens
                if (pushedTokens.includes(x?.org)) return
                if (!x?.org) return

                const url = getRocketChatUrl(x.org)
                spLogTags(
                    'register push token for the rocketchat' +
                        url +
                        PushTokenSignal.value,
                    ['rocketchat']
                )
                apiClient.post(
                    url + ROCKET_ENDPOINTS.registerPush,
                    {
                        type: os === 'iOS' ? 'apn' : 'gcm',
                        value: PushTokenSignal.value,
                        appName: 'com.softpauer.eventr',
                    },
                    {
                        headers: {
                            'X-Auth-Token': x.authToken,
                            'X-User-Id': x.userId,
                        },
                    }
                )
                useBoundStore.setState({
                    pushedTokens: [...pushedTokens, x.org],
                })
            })
        }
    }, [rcTokens, apiClient, PushTokenSignal.value])

    function openChat(roomId?: string, chatType?: string) {
        spLogTags(`Navigating to page: /rocketchat/${chatType}/${roomId}`, [
            'rocketchat',
        ])
        if (roomId) {
            window.pushPath(`/rocketchat/${chatType}/${roomId}`)
        } else {
            defaultAddSnack(i18n.t('snack.rocket.unableToFindChat'), 'error')
        }
    }

    const createDirectChannel = async (
        data: RocketCreateDirectChannelBodyType
    ) => {
        spLogTags(`Creating new direct chat with: ${data.user_id2} `, [
            'rocketchat',
        ])
        const resp = await RocketAPI.createDirectChannel(data)
        spLogTags(resp, ['rocketchat'])
        getRooms()
        return resp
    }

    function constructMethod(method: string, params?: any, id?: string) {
        return JSON.stringify({
            msg: 'method',
            method: method,
            id: id ?? '8129',
            params: params,
        })
    }

    function subscribeToStream(name: string, uuid: string, params: any) {
        if (rocketWebsocket.current && isOpen()) {
            try {
                rocketWebsocket.current.send(
                    JSON.stringify({
                        msg: 'sub',
                        id: uuid,
                        name: name,
                        params: params,
                    })
                )
                return uuid
            } catch (e) {
                spLogTags('Could not subscribe to stream', [
                    'rocketchat',
                    'error',
                ])
                spLogTags(e, ['rocketchat', 'error'])
                return null
            }
        } else {
            spLogTags('Websocket is closed, cannot subscribe to stream', [
                'rocketchat',
                'error',
            ])
            return null
        }
    }

    function isOpen() {
        if (rocketWebsocket && rocketWebsocket.current) {
            try {
                const tempOpen =
                    rocketWebsocket.current.readyState ===
                    rocketWebsocket.current.OPEN
                return tempOpen
            } catch {
                spErrorTags('Error with websocket', ['rocketchat'])
                return false
            }
        } else {
            return false
        }
    }

    async function markAsRead(rid: string) {
        const url = getRocketChatUrl(liveToken?.value?.org ?? '')
        const resp = await apiClient
            .post(
                url + ROCKET_ENDPOINTS.readSubscriptions,
                {
                    rid: rid,
                },
                {
                    headers: {
                        'X-Auth-Token': liveToken?.value?.authToken ?? '',
                        'X-User-Id': liveToken?.value?.userId ?? '',
                    },
                }
            )
            .catch((error) => {
                spLogTags(error, ['rocketchat', 'ERROR'])
            })
        if (resp && resp.data && (resp.data as any).success) {
            return (resp.data as any).success
        } else {
            spLogTags('Error changing unread, invalid response', [
                'ERROR',
                'rocketchat',
            ])
            return false
        }
    }

    useEffect(() => {
        async function getUsersList(
            options: {
                query?: object | undefined
                field?: object | undefined
            } = {
                query: undefined,
                field: undefined,
            }
        ) {
            const url = getRocketChatUrl(liveToken?.value?.org ?? '')
            const query = options?.query
                ? `&query=${JSON.stringify(options.query)}`
                : ''
            const field = options?.field
                ? `&fields=${JSON.stringify(options.field)}`
                : ''
            try {
                const users = await fetch(
                    `${url}${ROCKET_ENDPOINTS.usersList}${query}${field}&count=0`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Auth-Token': liveToken?.value?.authToken ?? '',
                            'X-User-Id': liveToken?.value?.userId ?? '',
                        },
                        method: 'GET',
                    }
                )
                const resp = await users.json()
                chatStore.value?.set(
                    'users',
                    'rcUsers-' + (liveToken?.value?.org ?? ''),
                    resp
                )
                setRcUserList(resp.users)
                return resp
            } catch (err) {
                spLogTags('Error fetching rcUsers', ['rocketchat', 'ERROR'])
            }
        }

        if (selectedOrg && wpUser) {
            getChatCache('users', 'rcUsers-' + selectedOrg.name).then(
                (e: any) => {
                    setRcUserList(e?.users)
                }
            )

            if (liveToken.value) {
                spLogTags(
                    'Getting RC users information and writing to chatstore',
                    ['rocketchat']
                )
                getUsersList({
                    field: {
                        _id: 1,
                        username: 1,
                        name: 1,
                        status: 1,
                        type: 1,
                        customFields: 1,
                    },
                }).then((resp) => {
                    if (resp && resp.success) {
                        spLogTags('getUsersList, success: ' + resp.success, [
                            'rocketchat',
                        ])
                    } else {
                        spLogTags(resp, ['rocketchat', 'ERROR'])
                    }
                })
            }
        }
    }, [selectedOrg, liveToken.value, setRcUserList, wpUser])

    useEffect(() => {
        async function getUserInfo() {
            const url = getRocketChatUrl(liveToken?.value?.org ?? '')
            try {
                const userInfo = await fetch(url + ROCKET_ENDPOINTS.userInfo, {
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Auth-Token': liveToken?.value?.authToken ?? '',
                        'X-User-Id': liveToken?.value?.userId ?? '',
                    },
                    method: 'GET',
                })
                const resp = await userInfo.json()
                chatStore.value?.set(
                    'me',
                    'rcUserInfo-' + (liveToken?.value?.org ?? ''),
                    resp
                )
                setRcUserInfo(resp)
                return resp
            } catch (err) {
                spLogTags('Error fetching rocketchat userInfo', [
                    'rocketchat',
                    'ERROR',
                ])
            }
        }

        if (selectedOrg && wpUser) {
            getChatCache('me', 'rcUserInfo-' + selectedOrg.name).then(
                (e: any) => {
                    setRcUserInfo(e)
                }
            )
            if (liveToken.value) {
                spLogTags('Getting RC profile and writing to chatstore', [
                    'rocketchat',
                ])
                getUserInfo().then((resp) => {
                    if (resp && resp.success) {
                    } else {
                        spLogTags(resp, ['rocketchat', 'ERROR'])
                    }
                })
            }
        }
    }, [selectedOrg, liveToken.value, setRcUserList, wpUser])

    const getRooms = useCallback(function (date?: number) {
        if (rocketWebsocket.current && isOpen()) {
            try {
                rocketWebsocket.current.send(
                    constructMethod('rooms/get', [{ $date: date ?? 0 }], '8122')
                )
                return
            } catch {
                spLogTags('Error getting rooms', ['rocketchat', 'error'])
                return
            }
        } else {
            spLogTags('Websocket is closed, cannot get rooms', [
                'rocketchat',
                'error',
            ])
            liveToken.value = undefined
            return
        }
    }, [])

    const getSubscribedRooms = useCallback(function (date?: number) {
        if (rocketWebsocket.current && isOpen()) {
            try {
                rocketWebsocket.current.send(
                    constructMethod(
                        'subscriptions/get',
                        [{ $date: date ?? 0 }],
                        '8123'
                    )
                )
                return
            } catch {
                spLogTags('Error getting rooms', ['rocketchat', 'error'])
                return
            }
        } else {
            spLogTags('Websocket is closed, cannot get rooms', [
                'rocketchat',
                'error',
            ])
            liveToken.value = undefined
            return
        }
    }, [])

    const resolveRooms = useCallback(
        function (subscriptions: any) {
            setUserSubscriptions(subscriptions)
        },
        [setUserSubscriptions]
    )

    const updateRoom = useCallback(
        (message: any) => {
            if (message.msg === 'changed') {
                if (
                    message.fields &&
                    message.fields.args &&
                    message.fields.args[0] === 'updated'
                ) {
                    let tempUserRooms = userRooms?.filter(
                        (room) => room._id !== message.fields.args[1]._id
                    )
                    tempUserRooms = tempUserRooms?.concat(
                        message.fields.args[1]
                    )
                    setUserRooms(tempUserRooms)
                } else if (
                    message.fields &&
                    message.fields.args &&
                    message.fields.args[0] === 'inserted'
                ) {
                    let tempUserRooms = userRooms?.concat(
                        message.fields.args[1]
                    )
                    setUserRooms(tempUserRooms)
                }
            }
        },
        [userRooms, setUserRooms]
    )

    const updateSubscription = useCallback(
        (message: any) => {
            if (message.msg === 'changed') {
                if (
                    message.fields &&
                    message.fields.args &&
                    message.fields.args[0] === 'updated'
                ) {
                    let tempUserSubscriptions = userSubscriptions?.filter(
                        (subscription) =>
                            subscription._id !== message.fields.args[1]._id
                    )
                    tempUserSubscriptions = tempUserSubscriptions?.concat(
                        message.fields.args[1]
                    )
                    setUserSubscriptions(tempUserSubscriptions)
                } else if (
                    message.fields &&
                    message.fields.args &&
                    message.fields.args[0] === 'inserted'
                ) {
                    let tempUserSubscriptions = userSubscriptions?.concat(
                        message.fields.args[1]
                    )
                    setUserSubscriptions(tempUserSubscriptions)
                }
            }
        },
        [userSubscriptions, setUserSubscriptions]
    )

    const handleNotification = useCallback(
        (message: any) => {
            if (message.msg === 'changed') {
                if (
                    message.fields.args &&
                    message.fields.args[0] &&
                    message.fields.args[0].payload
                ) {
                    let tempUserRooms: IRoom[] | undefined = []
                    userRooms?.forEach((room) => {
                        if (
                            room._id &&
                            message.fields.args[0].payload.rid &&
                            room._id === message.fields.args[0].payload.rid &&
                            room.lastMessage
                        ) {
                            const updatedRoom: IRoom = {
                                ...room,
                                lastMessage: {
                                    ...room.lastMessage,
                                    msg:
                                        message.fields.args[0].payload.message
                                            ?.msg ?? '',
                                },
                                lm: new EventrDate().getUnixMs,
                            }

                            tempUserRooms = [
                                ...(tempUserRooms ?? []),
                                updatedRoom,
                            ]
                        } else {
                            tempUserRooms = tempUserRooms?.concat(room)
                        }
                    })
                    setUserRooms(tempUserRooms)
                }
            }
        },
        [userRooms, setUserRooms]
    )

    const subscribeToNotifications = useCallback(() => {
        if (liveToken.value && isOpen()) {
            subscribeToStream('stream-notify-user', '8129', [
                `${liveToken?.value?.userId}/message`,
            ])
        }
        if (liveToken.value && isOpen()) {
            subscribeToStream('stream-notify-user', '8130', [
                `${liveToken?.value?.userId}/webrtc`,
            ])
        }
        if (liveToken.value && isOpen()) {
            subscribeToStream('stream-notify-user', '8131', [
                `${liveToken?.value?.userId}/notification`,
            ])
        }
        if (liveToken.value && isOpen()) {
            subscribeToStream('stream-notify-user', '8132', [
                `${liveToken?.value?.userId}/rooms-changed`,
            ])
        }
        if (liveToken.value && isOpen()) {
            subscribeToStream('stream-notify-user', '8133', [
                `${liveToken?.value?.userId}/subscriptions-changed`,
            ])
        }
        setLoadingChat(false)
    }, [liveToken.value, setLoadingChat])

    useEffect(() => {
        if (initialiseRooms && wpUser && isOpen()) {
            setInitialiseRooms(false)
        }
    }, [
        initialiseRooms,
        setInitialiseRooms,
        wpUser,
        rocketWebsocket.current && rocketWebsocket.current.readyState,
    ])

    const handleMessages = useCallback(
        (message: any) => {
            if (message.msg === 'ping') {
                try {
                    if (rocketWebsocket.current && isOpen()) {
                        rocketWebsocket.current.send(
                            JSON.stringify({
                                msg: 'pong',
                            })
                        )
                    } else {
                        spLogTags('Websocket not open', ['rocketchat'])
                    }
                } catch {
                    spLogTags('Could not send pong, did the websocket close?', [
                        'rocketchat',
                    ])
                }
                return
            } else if (message.msg === 'connected') {
                try {
                    if (rocketWebsocket.current && isOpen()) {
                        rocketWebsocket.current.send(
                            constructMethod(
                                'login',
                                [{ resume: liveToken?.value?.authToken }],
                                // loginUuid
                                '8121'
                            )
                        )
                    } else {
                        spLogTags('Websocket not open', ['rocketchat'])
                    }
                } catch (error) {
                    spLogTags(error, ['rocketchat'])
                }
                return
            } else if (
                message.id &&
                message.id ===
                    // loginUuid
                    '8121' &&
                message.msg === 'result' &&
                message.result
            ) {
                try {
                    subscribeToNotifications()
                    getRooms()
                    getSubscribedRooms()
                } catch (error) {
                    spLogTags(error, ['rocketchat'])
                }
            } else if (
                message.id &&
                message.id ===
                    // getRoomsUuid
                    '8122' &&
                message.msg === 'result'
            ) {
                if (message.result) {
                    if (message.result.update) {
                        setUserRooms(message.result.update)
                        chatStore.value?.set(
                            'rooms',
                            'rcRooms-' + liveToken?.value?.org,
                            message.result.update
                        )
                    } else {
                        spLogTags('Error fetching userRooms', [
                            'rocketchat',
                            'ERROR',
                        ])
                    }
                } else {
                    spLogTags('Error fetching userRooms, no result', [
                        'rocketchat',
                        'ERROR',
                    ])
                }
            } else if (
                message.id &&
                message.id ===
                    // getSubscribedRoomsUuid
                    '8123' &&
                message.msg === 'result'
            ) {
                if (message.result) {
                    resolveRooms(message.result.update)
                    chatStore.value?.set(
                        'subscriptions',
                        'rcSubscriptions-' + (liveToken?.value?.org ?? ''),
                        message.result.update
                    )
                } else {
                    spLogTags('Error fetching userSubscriptions', [
                        'rocketchat',
                        'ERROR',
                    ])
                }
            } else if (message.collection === 'stream-notify-user') {
                if (
                    message.fields.eventName ===
                    `${liveToken?.value?.userId}/message`
                ) {
                    try {
                    } catch (e) {
                        spLogTags(
                            `Error in stream-notify-user, ${liveToken?.value?.userId}/message`,
                            ['rocketchat', 'ERROR']
                        )
                        spLogTags(e, ['rocketchat', 'ERROR'])
                    }
                }
                if (
                    message.fields.eventName ===
                    `${liveToken?.value?.userId}/webrtc`
                ) {
                    try {
                    } catch (e) {
                        spLogTags(
                            `Error in stream-notify-user, ${liveToken?.value?.userId}/webrtc`,
                            ['rocketchat', 'ERROR']
                        )
                        spLogTags(e, ['rocketchat', 'ERROR'])
                    }
                }
                if (
                    message.fields.eventName ===
                    `${liveToken?.value?.userId}/notification`
                ) {
                    try {
                        handleNotification(message)
                    } catch (e) {
                        spLogTags(
                            `Error in stream-notify-user, ${liveToken?.value?.userId}/notification`,
                            ['rocketchat', 'ERROR']
                        )
                        spLogTags(e, ['rocketchat', 'ERROR'])
                    }
                }
                if (
                    message.fields.eventName ===
                    `${liveToken?.value?.userId}/rooms-changed`
                ) {
                    try {
                        updateRoom(message)
                    } catch (e) {
                        spLogTags(
                            `Error in stream-notify-user, ${liveToken?.value?.userId}/rooms-changed`,
                            ['rocketchat', 'ERROR']
                        )
                        spLogTags(e, ['rocketchat', 'ERROR'])
                    }
                }
                if (
                    message.fields.eventName ===
                    `${liveToken?.value?.userId}/subscriptions-changed`
                ) {
                    try {
                        updateSubscription(message)
                    } catch (e) {
                        spLogTags(
                            `Error in stream-notify-user, ${liveToken?.value?.userId}/subscriptions-changed`,
                            ['rocketchat', 'ERROR']
                        )
                        spLogTags(e, ['rocketchat', 'ERROR'])
                    }
                }
            }
        },
        [
            liveToken.value,
            getRooms,
            resolveRooms,
            getSubscribedRooms,
            updateRoom,
            updateSubscription,
            handleNotification,
            subscribeToNotifications,
            initialiseRooms,
        ]
    )

    useEffect(() => {
        try {
            if (isActivated) {
                if (selectedOrg) {
                    if (isConnectivityAvailable.value) {
                        if (liveToken.value && liveToken.value.authToken) {
                            if (
                                liveToken.value.org === selectedOrg.name &&
                                rcUrl &&
                                rcUrl !== ''
                            ) {
                                spLogTags(
                                    'RocketChat token: ' +
                                        liveToken.value.authToken +
                                        ', for the org: ' +
                                        liveToken.value.org,
                                    ['rocketchat']
                                )
                                rocketWebsocket.current = new WebSocket(rcUrl)
                                rocketWebsocket.current.onopen = (e) => {
                                    spLogTags(
                                        'Websocket opened at url ' + rcUrl,
                                        ['rocketchat']
                                    )

                                    try {
                                        if (
                                            rocketWebsocket.current &&
                                            isOpen()
                                        ) {
                                            rocketWebsocket.current.send(
                                                JSON.stringify({
                                                    msg: 'connect',
                                                    version: '1',
                                                    support: ['1'],
                                                })
                                            )
                                        } else {
                                            spLogTags('Websocket not open', [
                                                'rocketchat',
                                            ])
                                        }
                                    } catch (error) {
                                        spError(error)
                                    }
                                }

                                rocketWebsocket.current.onclose = (e) => {
                                    spLogTags('Websocket closed ' + e.code, [
                                        'rocketchat',
                                    ])
                                    setInitialiseRooms(true)
                                }

                                return () => {
                                    rocketWebsocket?.current?.close()
                                }
                            }
                        } else {
                            spLogTags(
                                'No valid rocketchat token to open websocket',
                                ['rocketchat']
                            )
                            if (liveToken.value === undefined) {
                                if (rcTokens === undefined) {
                                    bs().getRcTokens()
                                } else {
                                    getTokens()
                                }
                            } else {
                                spErrorTags(
                                    'Live token is invalid or cannot be reached',
                                    ['rocketchat', 'ERROR']
                                )
                            }
                        }
                    } else {
                        spLogTags(
                            'No network connection, cannot open websocket',
                            ['rocketchat']
                        )
                    }
                } else {
                    spLogTags('No org selected, cannot get token', [
                        'rocketchat',
                    ])
                }
                return
            } else {
                spLogTags(
                    'RocketChat is not activated so will not open websocket',
                    ['rocketchat']
                )
            }
        } catch (e) {
            spErrorTags(e, ['Error opening websocket'])
        }
    }, [
        rcUrl,
        liveToken.value,
        selectedOrg,
        rcTokens,
        isActivated,
        getTokens,
        isConnectivityAvailable.value,
    ])
    useEffect(() => {
        if (isActivated) {
            if (rocketWebsocket.current) {
                if (liveToken.value) {
                    rocketWebsocket.current.onmessage = (e) => {
                        const message = JSON.parse(e.data)
                        handleMessages(message)
                    }
                }
            } else {
                !loadingChat &&
                    spLogTags('Websocket is closed, cannot handle message', [
                        'rocketchat',
                    ])
                liveToken.value = undefined
            }
        }
    }, [handleMessages, isActivated, loadingChat])

    const setRooms = (roomId: string) => {
        setUserRooms(userRooms?.filter((room) => room._id !== roomId))
    }

    return (
        <RocketChatContext.Provider
            value={{
                userRooms: userRooms ?? [],
                userSubscriptions: userSubscriptions ?? [],
                rcUserList: rcUserList ?? [],
                rcUserInfo,
                createDirectChannel,
                openChat,
                loadingChat,
                setLoadingChat,
                isActivated,
                searchInput,
                setSearchInput,
                getRooms,
                setRooms,
                getSubscribedRooms,
                markAsRead,
                chatView: chatView,
            }}
        >
            {props.children}
        </RocketChatContext.Provider>
    )
}

export const useRocketChatContext = () => {
    return useContext(RocketChatContext)
}
export const getChatTypeFromSectionId = (section_id: number) => {
    const chatTypes = {
        1: 'Group',
        2: 'Flight',
        3: 'Accommodation',
        4: 'HireCar',
        5: 'Transport',
        8: 'Activity',
        9: 'ParkingBooking',
        10: 'VehicleAllocation',
    }

    return chatTypes[section_id as keyof typeof chatTypes] || 'User'
}
export const getSectionIdFromChatType = (chatType: string) => {
    const chatTypeMap: { [key: string]: string } = {
        Group: '1',
        Flight: '2',
        Accommodation: '3',
        HireCar: '4',
        Transport: '5',
        Activity: '8',
        ParkingBooking: '9',
        VehicleAllocation: '10',
    }

    return chatTypeMap[chatType] || '0'
}
